import type { PhysicsStats } from '../physics/types';

export type AvbdRuntimeOverlayState = {
  profile: string;
  substeps: number;
  solverIterations: number;
  adaptiveIterationsConfigured: boolean;
  adaptiveMinIterations: number;
  adaptiveSelectedLast: boolean;
  completedSolveAvailable: boolean;
  iterationsLast: number;
  iterationsAverage: number;
  adaptiveEarlyExits: number;
  alpha: number;
  beta: number;
  betaAngular: number;
  gamma: number;
  kStart: number;
};

export class DebugOverlay {
  private infoDiv: HTMLDivElement;
  private metricsPre: HTMLPreElement;
  private startTimeMs: number;
  private rafFps = 0;
  private rafSampleStartMs: number;
  private rafSampleFrames = 0;
  private renderFps = 0;
  private renderSampleStartMs: number;
  private renderSampleFrames = 0;
  private simulationHz = 0;
  private simulationSampleStartMs: number;
  private simulationSampleStepCount: number | null = null;
  private lastMetricsRenderMs = Number.NEGATIVE_INFINITY;

  constructor() {
    this.startTimeMs = performance.now();
    this.rafSampleStartMs = this.startTimeMs;
    this.renderSampleStartMs = this.startTimeMs;
    this.simulationSampleStartMs = this.startTimeMs;

    this.infoDiv = document.createElement('div');
    this.infoDiv.dataset.testid = 'avbd-runtime-card';
    Object.assign(this.infoDiv.style, {
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      color: '#f8fafc',
      fontFamily: 'Inter, ui-monospace, SFMono-Regular, Consolas, monospace',
      fontSize: '12px',
      background: 'linear-gradient(145deg, rgba(8, 12, 20, 0.93), rgba(13, 24, 33, 0.88))',
      border: '1px solid rgba(104, 211, 184, 0.28)',
      boxShadow: '0 18px 50px rgba(0, 0, 0, 0.35)',
      padding: '11px 13px',
      borderRadius: '10px',
      lineHeight: '1.45',
      backdropFilter: 'blur(12px)',
      zIndex: '9',
    });

    const heading = document.createElement('div');
    heading.textContent = 'PMavbd · WebGPU AVBD';
    Object.assign(heading.style, {
      color: '#9ff7df',
      fontFamily: 'system-ui, sans-serif',
      fontWeight: '750',
      fontSize: '13px',
      letterSpacing: '0.02em',
      marginBottom: '5px',
    });

    this.metricsPre = document.createElement('pre');
    this.metricsPre.dataset.testid = 'avbd-runtime-stats';
    Object.assign(this.metricsPre.style, {
      margin: '0',
      font: 'inherit',
      whiteSpace: 'pre',
    });

    const links = document.createElement('div');
    Object.assign(links.style, {
      display: 'flex',
      gap: '10px',
      marginTop: '7px',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
    });
    links.append(
      this.makeLink('运行 GPU 验证', './avbd-regression.html', 'verification-link'),
      this.makeLink('510K 全链', './wide-510k-end-to-end.html', 'wide-510k-link'),
      this.makeLink(
        '论文',
        'https://graphics.cs.utah.edu/research/projects/avbd/Augmented_VBD-SIGGRAPH25.pdf',
        'paper-link',
      ),
    );

    this.infoDiv.append(heading, this.metricsPre, links);
    document.body.appendChild(this.infoDiv);
  }

  private makeLink(label: string, href: string, testId: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.textContent = label;
    link.href = href;
    link.dataset.testid = testId;
    link.target = '_blank';
    link.rel = 'noreferrer';
    Object.assign(link.style, {
      color: '#70e1c2',
      textDecoration: 'none',
      borderBottom: '1px solid rgba(112, 225, 194, 0.42)',
    });
    return link;
  }

  begin(): void {
    // Kept for the render-loop timing contract; sampling happens in end().
  }

  end(presented = true): void {
    this.rafSampleFrames++;
    if (presented) this.renderSampleFrames++;
    this.sampleFrameRates(performance.now());
  }

  reset(): void {
    const now = performance.now();
    this.startTimeMs = now;
    this.rafFps = 0;
    this.rafSampleStartMs = now;
    this.rafSampleFrames = 0;
    this.renderFps = 0;
    this.renderSampleStartMs = now;
    this.renderSampleFrames = 0;
    this.simulationHz = 0;
    this.simulationSampleStartMs = now;
    this.simulationSampleStepCount = null;
    this.lastMetricsRenderMs = Number.NEGATIVE_INFINITY;
  }

  update(phys: PhysicsStats, avbd?: AvbdRuntimeOverlayState): void {
    const now = performance.now();
    this.sampleFrameRates(now);
    if (this.simulationSampleStepCount === null) {
      this.simulationSampleStepCount = phys.simulationStepCount;
      this.simulationSampleStartMs = now;
    } else {
      const simulationSampleElapsedMs = now - this.simulationSampleStartMs;
      if (simulationSampleElapsedMs >= 500) {
        const completedSteps = Math.max(0, phys.simulationStepCount - this.simulationSampleStepCount);
        this.simulationHz = completedSteps * 1000 / simulationSampleElapsedMs;
        this.simulationSampleStartMs = now;
        this.simulationSampleStepCount = phys.simulationStepCount;
        document.documentElement.dataset.avbdSimulationHz = this.simulationHz.toFixed(1);
      }
    }

    // The render loop calls update on every rAF, including backpressure-only
    // ticks. Refreshing this text four times per second keeps the diagnostics
    // responsive without turning the overlay into a per-frame DOM workload.
    if (now - this.lastMetricsRenderMs < 250) return;
    this.lastMetricsRenderMs = now;

    const elapsedSeconds = (now - this.startTimeMs) / 1000;
    const telemetry = document.documentElement.dataset;
    const queueState = telemetry.gpuQueueState ?? 'starting';
    const framesInFlight = telemetry.gpuFramesInFlight ?? '0';
    const frameLimit = telemetry.gpuFrameLimit ?? '?';
    const fenceEmaMs = telemetry.gpuFenceEmaMs ?? '0.0';
    const skippedFrames = telemetry.gpuSubmissionSkippedFrames ?? '0';
    const lines = [
      `rAF FPS            ${this.rafFps.toFixed(1)}`,
      `Render FPS         ${this.renderFps.toFixed(1)}`,
      `Simulation Hz      ${this.simulationHz.toFixed(1)}`,
      `Bodies             ${phys.bodyCount}`,
      `Frame              ${phys.frameCount}`,
      `Simulation steps   ${phys.simulationStepCount}`,
      `GPU queue          ${queueState} / ${framesInFlight}/${frameLimit} in flight`,
      `Fence EMA / skips  ${fenceEmaMs} ms / ${skippedFrames}`,
      `Broadphase         ${phys.broadPhaseReady ? 'ready' : 'waiting'} / ${phys.candidatePairsEnabled ? 'candidates' : 'no candidates'}`,
      `Capacity graph     ${phys.pairDispatchTruncated ? 'INCOMPLETE - PAUSED' : 'complete'}`,
      `Elapsed            ${elapsedSeconds.toFixed(1)} s`,
    ];
    if (avbd) {
      const sweepsPerFrame = avbd.substeps * avbd.solverIterations;
      const configuredPolicy = avbd.adaptiveIterationsConfigured
        ? `adaptive (min ${avbd.adaptiveMinIterations})`
        : 'fixed';
      const actualPolicy = !avbd.completedSolveAvailable
        ? 'pending first solve'
        : avbd.adaptiveSelectedLast
        ? 'adaptive selected'
        : avbd.adaptiveIterationsConfigured
        ? 'fixed fallback'
        : 'fixed configured';
      lines.push(
        `AVBD profile       ${avbd.profile}`,
        `Solver iterations  ${avbd.solverIterations} × ${avbd.substeps} substep`,
        `Iteration config   ${configuredPolicy}`,
        `Iteration actual   ${actualPolicy}`,
        `Iterations last/avg ${avbd.iterationsLast} / ${avbd.iterationsAverage.toFixed(2)}`,
        `Adaptive exits     ${avbd.adaptiveEarlyExits}`,
        `Primal / dual      ${sweepsPerFrame} / ${sweepsPerFrame} passes`,
        `α / βL / βA / γ    ${avbd.alpha.toFixed(3)} / ${avbd.beta.toFixed(0)} / ${avbd.betaAngular.toFixed(0)} / ${avbd.gamma.toFixed(5)}`,
        `k start            ${avbd.kStart.toExponential(1)}`,
      );
    }
    const metricsText = lines.join('\n');
    if (this.metricsPre.textContent !== metricsText) {
      this.metricsPre.textContent = metricsText;
    }
  }

  private sampleFrameRates(now: number): void {
    const rafElapsedMs = now - this.rafSampleStartMs;
    if (rafElapsedMs >= 500) {
      this.rafFps = this.rafSampleFrames * 1000 / rafElapsedMs;
      this.rafSampleStartMs = now;
      this.rafSampleFrames = 0;
      document.documentElement.dataset.avbdRafFps = this.rafFps.toFixed(1);
    }

    const renderElapsedMs = now - this.renderSampleStartMs;
    if (renderElapsedMs < 500) return;
    this.renderFps = this.renderSampleFrames * 1000 / renderElapsedMs;
    this.renderSampleStartMs = now;
    this.renderSampleFrames = 0;
    document.documentElement.dataset.avbdFps = this.renderFps.toFixed(1);
  }
}
